<template>
  <div v-if="billingEntitlementsReady"
       class="billing-feature-notice"
       :class="[ `billing-feature-notice--${variant}`, { 'billing-feature-notice-border': bordered }]">
    <div class="billing-feature-notice__icon">
      <i :class="icon"/>
    </div>

    <div class="billing-feature-notice__content">
      <div v-if="title"
           class="billing-feature-notice__title">
        {{ title }}
      </div>

      <div class="billing-feature-notice__plan">
        <template v-if="resolvedRequiredPlanKey">
          {{
            $t('billing.featureGate.availableFromPlanLabel', {
              plan: resolvedRequiredPlanKey
            })
          }}
        </template>

        <template v-else>
          {{ $t('billing.featureGate.unavailableOnCurrentPlanLabel') }}
        </template>
      </div>

      <div v-if="$slots.default || description"
           class="billing-feature-notice__description">
        <slot>
          {{ description }}
        </slot>
      </div>

      <div v-if="variant === 'page' &&
           currentLocationPlanKey &&
           !$store.getters.isCurrentLocationBillingLocked"
           class="billing-feature-notice__current-plan">
        {{
          $t('billing.featureGate.currentPlanLabel', {
            plan: currentLocationPlanKey
          })
        }}
      </div>

      <div v-if="canOpenSubscriptions || showOwnerHint"
           class="billing-feature-notice__actions">
        <Button v-if="canOpenSubscriptions"
                style="width: auto"
                :label="$t('billing.featureGate.changePlanLabel')"
                icon="ti-arrow-right"
                iconPos="right"
                class="p-button-raised"
                :class="{ 'p-button-sm': variant === 'compact' }"
                @click="openSubscriptions"/>

        <div v-else-if="showOwnerHint"
             class="billing-feature-notice__owner-hint">
          <i class="pi pi-info-circle"/>
          <span>{{ $t('billing.featureGate.contactOwnerMsg') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BillingFeatureNotice',

  props: {
    feature: {
      type: String,
      default: '',
    },

    requiredPlanKey: {
      type: String,
      default: '',
    },

    variant: {
      type: String,
      default: 'compact',
      validator: value => ['compact', 'page'].includes(value),
    },

    title: {
      type: String,
      default: '',
    },

    description: {
      type: String,
      default: '',
    },

    icon: {
      type: String,
      default: 'ti-lock',
    },

    bordered: {
      type: Boolean,
      default: false,
    },

    openInNewTab: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    billingEntitlementsReady() {
      return this.$store.getters.billingEntitlementsReady
    },

    requirement() {
      if (!this.feature) return null

      return this.$store.getters.requiredPlanFor(this.feature)
    },

    resolvedRequiredPlanKey() {
      return this.requiredPlanKey || this.requirement?.minimum_plan_key || null
    },

    currentLocationPlanKey() {
      return this.$store.getters.currentLocationPlanKey || null
    },

    canOpenSubscriptions() {
      return (
          this.$store.getters.isOwnerOrHigher &&
          !this.$store.getters.isCurrentLocationBillingLocked &&
          !!this.currentLocationPlanKey
      )
    },

    showOwnerHint() {
      return (
          !this.$store.getters.isOwnerOrHigher &&
          !this.$store.getters.isCurrentLocationBillingLocked &&
          !!this.currentLocationPlanKey
      )
    },
  },

  methods: {
    // openSubscriptions() {
    //   const path = '/account/subscriptions'
    //
    //   if (this.$route.path === path) {
    //     return
    //   }
    //
    //   this.$router.push(path)
    // },
    openSubscriptions() {
      const locationId = Number(this.$store.getters.locationId || 0)

      const query = { select_plan: '1' }

      if (this.feature) {
        query.required_feature = this.feature
      }

      if (locationId > 0) {
        query.location_id = String(locationId)
      }

      if (
          this.$route?.name === 'Subscriptions' &&
          String(this.$route.query.select_plan || '') === '1' &&
          String(this.$route.query.location_id || '') === String(locationId) &&
          String(this.$route.query.required_feature || '') === this.feature
      ) {
        return
      }

      const route = {
        name: 'Subscriptions',
        query,
      }

      if (this.openInNewTab) {
        const resolvedRoute = this.$router.resolve(route)
        window.open(resolvedRoute.href, '_blank', 'noopener')
        return
      }

      this.$router.push(route)
    },
  },
}
</script>

<style scoped lang="scss">
.billing-feature-notice-border {
  //padding: 24px 18px !important;
  border: 1px solid var(--border-color);
  border-radius: 3px;
  //font-size: 0.9rem;
}

.billing-feature-notice {
  //border: 1px solid var(--surface-border);
  //border-radius: 6px;
  //background: var(--surface-card);
  //color: var(--text-color);

  &--page {
    //min-height: 360px;
    padding: 3rem 2rem;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    text-align: center;
  }

  &--compact {
    //padding: 1.2rem;
    padding: 22px 18px;

    display: flex;
    align-items: flex-start;

    background: var(--surface-50);
  }

  &__icon {
    flex: 0 0 auto;

    display: flex;
    align-items: center;
    justify-content: center;

    color: var(--primary-color);
    border: 2px solid var(--primary-color);
    border-radius: 50%;
  }

  &--page &__icon {
    width: 64px;
    height: 64px;
    margin-bottom: 1.5rem;
    font-size: 1.75rem;
  }

  &--compact &__icon {
    width: 38px;
    height: 38px;
    margin-right: 1rem;
    font-size: 1rem;
  }

  &__content {
    min-width: 0;
  }

  &--page &__content {
    max-width: 620px;
  }

  &__title {
    font-weight: 600;
    color: var(--text-color);
  }

  &--page &__title {
    margin-bottom: 0.65rem;
    font-size: 1.65rem;
  }

  &--compact &__title {
    margin-bottom: 0.35rem;
    font-size: 1rem;
  }

  &__plan {
    font-weight: 600;
    color: var(--primary-color);
  }

  &--page &__plan {
    font-size: 1.05rem;
  }

  &__description {
    margin-top: 0.9rem;
    line-height: 1.55;
    color: var(--text-color-secondary);
  }

  &--compact &__description {
    margin-top: 0.5rem;
  }

  &__current-plan {
    margin-top: 1rem;
    font-size: 0.9rem;
    color: var(--text-color-secondary);
  }

  &__actions {
    margin-top: 1.5rem;
  }

  &--compact &__actions {
    margin-top: 1rem;
  }

  &__owner-hint {
    display: flex;
    align-items: center;
    justify-content: center;

    gap: 0.45rem;
    color: var(--text-color-secondary);
  }

  &--compact &__owner-hint {
    justify-content: flex-start;
  }
}
</style>